import Ajv, { ValidateFunction } from 'ajv'
import addErrors from 'ajv-errors'
import { IValidationResponse, IValidationRequest } from '../store/validation.types'
import * as allValidationMetadataArrays from '../schemas/'
import * as allValidationMetadataSecondsArrays from '../schemas/timeSeconds'
import { IActionType } from '../../../common/common.types'
import { getNumberOfNonNestedKeysInObject } from '../../../utilities/object.util'
import { isValidNemsisDate, isValidNemsisDOB, isValidZollNowDate } from '../../../utilities/dateTime.util'
import { HomeConstants } from '../../home/store/home.constants'
import { buildInputValidationId } from '../util/validation.util'
import { VitalsConstants } from '../../vitals/store/vitals.constants'

export default class ValidationService {
  private static _SET_ACTION_TYPE_TO_VALIDATION_METADATA_MAP: Map<string, any> = new Map()

  private static _INTERNAL_SET_ACTION_TYPE_TO_VALIDATE_FUNCTION_MAP: Map<string, ValidateFunction> = new Map()

  private static _INTERNAL_ACTION_TYPES_THAT_RESET_VALIDATION_SET: Set<string> = new Set()

  private static _INTERNAL_ID_TO_VALIDATION_ELEMENT_MAP: Map<string, HTMLElement> = new Map()

  private static jsonValidator = new Ajv({
    allErrors: true,
    verbose: true,
    strict: false,
  })

  private static ajvInitialized = false

  static resetState() {
    ValidationService._INTERNAL_ID_TO_VALIDATION_ELEMENT_MAP.forEach((element) => {
      element.innerHTML = ''
    })

    ValidationService._INTERNAL_ID_TO_VALIDATION_ELEMENT_MAP = new Map()
  }

  static clearValidation(id: string, cardinalityKey?: string) {
    const element = ValidationService.getInputValidationElementForId(id, cardinalityKey)

    if (element) {
      element.innerHTML = ''
    }
  }

  static isValidationMetadataForTypeNumber(setActionType: string): boolean {
    const validationMetadata = ValidationService.getValidationMetadataForSetActionType(setActionType)

    return validationMetadata.isNumber ? validationMetadata.isNumber : false
  }

  static getValidateFunction(setActionType: string): ValidateFunction {
    let validateFunction = ValidationService._INTERNAL_SET_ACTION_TYPE_TO_VALIDATE_FUNCTION_MAP.get(setActionType)

    // Lazy initialization
    // TODO: Consider better memory management in the future (e.g. use an LRU cache)
    if (!validateFunction) {
      const validationMetadata = ValidationService.getValidationMetadataForSetActionType(setActionType)

      validateFunction = ValidationService.jsonValidator.compile(validationMetadata)

      if (!validateFunction) {
        throw Error(
          `Unable to build validateFunction for validationMetadata: ${validationMetadata} using setActionType: ${setActionType}`
        )
      }

      ValidationService._INTERNAL_SET_ACTION_TYPE_TO_VALIDATE_FUNCTION_MAP.set(setActionType, validateFunction)
    }

    return validateFunction
  }

  static validate(iValidationRequest: IValidationRequest): IValidationResponse {
    const validator = iValidationRequest.validateFunction

    const isValid = validator(iValidationRequest.dataToValidate)

    let isRuleReady = true

    let errorMessage = ''
    const newValue = iValidationRequest.currentValue?.trim()

    if (!isValid && validator.errors && validator.errors.length > 0 && validator.errors[0].message) {
      errorMessage = validator.errors[0].message
      isRuleReady = false
    }

    if (newValue.length === 0) {
      errorMessage = ''
      isRuleReady = false
    }

    return {
      currentAction: iValidationRequest.currentAction,
      currentInput: iValidationRequest.currentInput,
      currentValue: iValidationRequest.currentValue,
      errorMessage,
      isValid,
      newValue,
      isRuleReady,
    }
  }

  static getNamespaceForType(setActionType: string): string {
    return setActionType.substring(0, ValidationService.getIndexOfSlash(setActionType))
  }

  static getActionType(setActionType: string): string {
    return setActionType.substring(ValidationService.getIndexOfSlash(setActionType) + 1)
  }

  static getInputValidationElementForId(id: string, cardinalityKey?: string): HTMLElement | null {
    const lookUpId = buildInputValidationId(id, cardinalityKey)

    const element = document.getElementById(lookUpId)

    if (element) {
      ValidationService._INTERNAL_ID_TO_VALIDATION_ELEMENT_MAP.set(lookUpId, element)
    }

    return element
  }

  static actionTypeShouldBeValidated(actionType: IActionType): boolean {
    if (!actionType || !actionType.type) {
      return false
    }

    const numOfNonNestedKeys = getNumberOfNonNestedKeysInObject(actionType)

    if (numOfNonNestedKeys < 2) {
      return false
    }

    const type = actionType.type

    return type.indexOf('/SET_') !== -1 && ValidationService.hasValidationMetadata(type)
  }

  static actionTypeShouldResetValidation(actionType: IActionType): boolean {
    if (!actionType) {
      return false
    }

    return ValidationService._INTERNAL_ACTION_TYPES_THAT_RESET_VALIDATION_SET.has(actionType.type)
  }

  static initialize() {
    if (!ValidationService.ajvInitialized) {
      addErrors(ValidationService.jsonValidator)

      ValidationService.jsonValidator.addFormat('nemsis-date', isValidNemsisDate)
      ValidationService.jsonValidator.addFormat('nemsis-dob', isValidNemsisDOB)
      ValidationService.jsonValidator.addFormat('zoll-now-date', isValidZollNowDate)

      ValidationService.ajvInitialized = true
    }

    ValidationService.initializeSetActionTypeToValidationMetadataMap()

    ValidationService.initializeActionTypesThatResetValidationSet()
    ValidationService.resetInternalSetActionTypeToValidateFunctionMap()
  }

  private static initializeSetActionTypeToValidationMetadataMap() {
    Object.values(allValidationMetadataArrays).forEach((allValidationMetadataArray) => {
      Object.values(allValidationMetadataArray).forEach((validationMetadata) => {
        ValidationService._SET_ACTION_TYPE_TO_VALIDATION_METADATA_MAP.set(
          validationMetadata.inputKey,
          validationMetadata
        )
      })
    })
  }

  public static initialSetTimeWithSeconds(useSeconds: boolean) {
    if (!useSeconds) {
      return
    }
    if (useSeconds) {
      Object.values(allValidationMetadataSecondsArrays).forEach((allValidationMetadataSecondsArray) => {
        Object.values(allValidationMetadataSecondsArray).forEach((validationMetadata) => {
          ValidationService._SET_ACTION_TYPE_TO_VALIDATION_METADATA_MAP.set(
            validationMetadata.inputKey,
            validationMetadata
          )
        })
      })
    }
  }

  private static initializeActionTypesThatResetValidationSet() {
    ValidationService._INTERNAL_ACTION_TYPES_THAT_RESET_VALIDATION_SET.add(HomeConstants.RESET_VALIDATIONS)
    ValidationService._INTERNAL_ACTION_TYPES_THAT_RESET_VALIDATION_SET.add(HomeConstants.SET_NEW_PCR)
    ValidationService._INTERNAL_ACTION_TYPES_THAT_RESET_VALIDATION_SET.add(HomeConstants.ON_SELECT_PCR)
    ValidationService._INTERNAL_ACTION_TYPES_THAT_RESET_VALIDATION_SET.add(VitalsConstants.RESET_PATIENT_VITALS_STATE)
  }

  private static resetInternalSetActionTypeToValidateFunctionMap() {
    ValidationService._INTERNAL_SET_ACTION_TYPE_TO_VALIDATE_FUNCTION_MAP.clear()
  }

  private static hasValidationMetadata(setActionType: string): boolean {
    return ValidationService._SET_ACTION_TYPE_TO_VALIDATION_METADATA_MAP.has(setActionType)
  }

  private static getValidationMetadataForSetActionType(setActionType: string): any {
    const validationMetadata = ValidationService._SET_ACTION_TYPE_TO_VALIDATION_METADATA_MAP.get(setActionType)

    if (!validationMetadata) {
      throw Error(`Validation metadata does not exist for set action type: ${setActionType}`)
    }

    return validationMetadata
  }

  private static getIndexOfSlash(setActionType: string): number {
    const indexOfSlash = setActionType.indexOf('/')

    if (indexOfSlash === -1) {
      throw Error(`Set Action Type: ${setActionType} does not contain a slash`)
    }

    return indexOfSlash
  }
}
